import { FilterTypes, SearchParams } from '@/constants/common';
import { buildUrlWithParams } from '@/utils/helpers';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { usePath } from '@/hooks/usePath';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';

const MAX_SEARCH_KEYWORDS = 10;
const SEARCH_ARTICLES = new Set(['a', 'an', 'the']);

const countSearchKeywords = (value: string) =>
  value
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0 && !SEARCH_ARTICLES.has(word.toLowerCase())).length;

export const useSearch = (filterType: FilterTypes) => {
  const router = useRouter();
  const pathname = usePath();
  const searchParams = useSearchParams();
  const { message } = App.useApp();
  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState('');
  const [showSearchInputReset, setShowInputReset] = useState(false);

  const searchUrlParam =
    filterType === FilterTypes.materials ? SearchParams.filterMaterialFileContentName : SearchParams.filterSearch;
  const search = searchParams.get(searchUrlParam);

  useEffect(() => {
    if (search) {
      if (searchValue !== search) setSearchValue(search);
      if (!showSearchInputReset) setShowInputReset(true);
    } else if (!search) {
      if (searchValue) setSearchValue('');
      if (showSearchInputReset) setShowInputReset(false);
    }
  }, [search]);

  const searchInputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const searchHandler = () => {
    setShowInputReset(true);

    const currentParams = new URLSearchParams(searchParams.toString());

    if (searchValue.trim() !== '') {
      if (searchUrlParam === SearchParams.filterSearch && countSearchKeywords(searchValue) > MAX_SEARCH_KEYWORDS) {
        message.error(t('common.filters.searchTooManyKeywords'));
        return;
      }
      currentParams.set(searchUrlParam, searchValue);
    } else {
      currentParams.delete(searchUrlParam);
    }

    const newUrl = buildUrlWithParams(pathname, '', Object.fromEntries(currentParams.entries()));
    router.push(newUrl);
  };

  const resetInputSearchHandler = () => {
    const currentParams = new URLSearchParams(searchParams.toString());
    currentParams.delete(searchUrlParam);

    setSearchValue('');
    setShowInputReset(false);

    const newUrl = buildUrlWithParams(pathname, '', Object.fromEntries(currentParams.entries()));
    router.push(newUrl);
  };

  return {
    searchValue,
    setSearchValue,
    showSearchInputReset,
    searchInputChangeHandler,
    searchHandler,
    resetInputSearchHandler,
    searchUrlParam,
  };
};
